/**
 * LevelWinrateByDayWidget - Винрейт уровня кампании по дням
 * ==========================================================
 *
 * Bar chart: X — календарная дата события, Y — винрейт уровня за день
 * (wins / (wins + losses), скипы не считаются). Диапазон дат дашборда
 * фильтрует дату события, а не регистрации; сегодняшний день не показывается.
 * Данные: GET /api/progression/level-winrate/by-day.
 */

import React, { useState } from 'react';
import { BaseWidget, useBaseWidget } from './BaseWidget';
import { useWidgetData } from '../../hooks/useWidgetData';
import { MetricsService } from '../../services/metricsService';
import { withDefaultDateRange } from '../../utils/defaultDateRange';
import { completedDaysSubtitle } from '../../utils/completedDaysWindow';
import { Input } from '../ui/input';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { Tooltip } from '../charts/OrderedTooltip';
import { ResponsiveChart as ResponsiveContainer } from '../ui/responsive-chart';
import { progression as progressionSpecs } from '@ggstat/chart-specs';

const LEVEL_MIN = 1;
const LEVEL_MAX = 2000;
// Уровень по умолчанию и ряд графика — общие с MCP (shared/chart-specs)
const { defaultLevel: DEFAULT_LEVEL, series: [WINRATE_SERIES] } = progressionSpecs.levelWinrate;

const formatInt = (value) => Number(value).toLocaleString('ru-RU');

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const point = payload[0].payload;
  return (
    <div className="bg-background border rounded-lg shadow-lg p-3">
      <p className="font-medium mb-2">{point.date}</p>
      <p className="text-sm">Винрейт: {point.winrate.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}%</p>
      <p className="text-sm">Победы: {formatInt(point.wins)}</p>
      <p className="text-sm">Поражения: {formatInt(point.losses)}</p>
      <p className="text-sm">Попытки: {formatInt(point.attempts)}</p>
    </div>
  );
};

export function LevelWinrateByDayWidget() {
  // Применённый уровень (уходит в запрос) и текст в поле ввода: запрос не
  // летит на каждое нажатие клавиши, только по Enter/потере фокуса.
  const [level, setLevel] = useState(DEFAULT_LEVEL);
  const [levelInput, setLevelInput] = useState(String(DEFAULT_LEVEL));

  const parsedInput = Number(levelInput);
  const isInputValid =
    /^\d+$/.test(levelInput.trim()) && parsedInput >= LEVEL_MIN && parsedInput <= LEVEL_MAX;

  const applyLevel = () => {
    if (isInputValid) {
      setLevel(parsedInput);
    } else {
      setLevelInput(String(level));
    }
  };

  // Диапазон дат обязателен: пустой → последние 30 дней (сервер иначе вернёт 400).
  const widgetData = useWidgetData(
    async (filters, options) =>
      MetricsService.getLevelWinrateByDay(withDefaultDateRange(filters), level, options),
    [level]
  );

  const widgetProps = useBaseWidget(widgetData);
  const { data } = widgetProps;
  const points = data?.data || [];

  const controls = (
    <label className="flex items-center gap-2 text-sm">
      Уровень
      <Input
        type="number"
        min={LEVEL_MIN}
        max={LEVEL_MAX}
        step={1}
        value={levelInput}
        onChange={(e) => setLevelInput(e.target.value)}
        onBlur={applyLevel}
        onKeyDown={(e) => e.key === 'Enter' && applyLevel()}
        aria-invalid={!isInputValid}
        className="w-24"
      />
    </label>
  );

  return (
    <BaseWidget
      {...widgetProps}
      data={points.length ? data : null}
      title={`Винрейт уровня ${level} по дням`}
      subtitle={completedDaysSubtitle('Период (дата события)', data?.dateRange)}
      headerAction={controls}
      emptyMessage="Нет попыток этого уровня за выбранный период"
      skeletonRows={8}
    >
      <ResponsiveContainer width="100%" height={420}>
        <BarChart data={points} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis dataKey="date" tick={{ fill: 'currentColor' }} className="text-xs" />
          <YAxis
            domain={[0, (max) => Math.min(100, Math.ceil(max / 10) * 10 + 10)]}
            tickFormatter={(v) => `${v}%`}
            tick={{ fill: 'currentColor' }}
            className="text-xs"
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar
            dataKey={WINRATE_SERIES.key}
            name={WINRATE_SERIES.label}
            fill={WINRATE_SERIES.color}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </BaseWidget>
  );
}

export default LevelWinrateByDayWidget;
